import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Check, Sparkles, PhoneCall, Inbox } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useUiLanguage } from '@/lib/i18n';
import useStore from '@/store/useStore';
import CustomerCombobox from '@/components/merchant/CustomerCombobox';
import CenteredStatusPopup from '@/components/merchant/CenteredStatusPopup';
import {
  apiMerchantDashboard,
  apiMerchantPendingRedemptions,
  apiMerchantInitiateRedemption,
  apiRejectRedemption,
  apiSimulateOrder,
  apiValidateRedemption,
} from '@/services/api';

export default function RewardsOperationsPage() {
  const { openEarnModal } = useStore();
  const { t } = useUiLanguage();
  const [dashboard, setDashboard] = useState(null);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSimulateCustomerId, setSelectedSimulateCustomerId] = useState('');
  const [selectedRedeemCustomerId, setSelectedRedeemCustomerId] = useState('');
  const [orderId, setOrderId] = useState('');
  const [amount, setAmount] = useState('');
  const [redeemPoints, setRedeemPoints] = useState('');
  const [redeemAmount, setRedeemAmount] = useState('');
  const [applyBonusOnRedemption, setApplyBonusOnRedemption] = useState(false);
  const [rejectedPendingIds, setRejectedPendingIds] = useState({});
  const [voiceConfig, setVoiceConfig] = useState({ enabled: false, target: 'all_callers', multiplier: '1x' });
  const [busyAction, setBusyAction] = useState('');
  const [toast, setToast] = useState('');
  const [toastType, setToastType] = useState('success');

  const customers = useMemo(() => dashboard?.customers || [], [dashboard?.customers]);
  const queue = useMemo(() => pending.filter((row) => !rejectedPendingIds[row.id]), [pending, rejectedPendingIds]);

  const setToastMessage = (message, type = 'success') => {
    setToast(message);
    setToastType(type);
    window.setTimeout(() => setToast(''), 3800);
  };

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [d, p] = await Promise.all([apiMerchantDashboard(), apiMerchantPendingRedemptions()]);
      setDashboard(d);
      setPending(p);
      if (!selectedSimulateCustomerId && d?.customers?.length) {
        setSelectedSimulateCustomerId(d.customers[0].id);
      }
      if (!selectedRedeemCustomerId && d?.customers?.length) {
        setSelectedRedeemCustomerId(d.customers[0].id);
      }
    } catch (e) {
      if (!silent) setToastMessage(t('Unable to load rewards data right now.'), 'error');
    }
    if (!silent) setLoading(false);
  }, [selectedSimulateCustomerId, selectedRedeemCustomerId, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const timer = setInterval(() => fetchData(true), 2500);
    return () => clearInterval(timer);
  }, [fetchData]);

  const runSimulateOrder = async () => {
    if (!selectedSimulateCustomerId) return;
    const cleanOrderId = (orderId || '').trim().toUpperCase();
    if (!/^ORD-\d{3}$/.test(cleanOrderId)) {
      setToastMessage(t('Order ID must follow ORD-123 format.'), 'error');
      return;
    }
    const amountNumber = Number(amount);
    if (!Number.isFinite(amountNumber) || amountNumber <= 0) {
      setToastMessage(t('Please enter a valid order amount.'), 'error');
      return;
    }
    setBusyAction('simulate');
    try {
      const res = await apiSimulateOrder(selectedSimulateCustomerId, cleanOrderId, amountNumber);
      openEarnModal(res);
      setOrderId('');
      setAmount('');
      setApplyBonusOnRedemption(false);
      await fetchData(true);
      setToastMessage(t('Points credited successfully.'));
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setToastMessage(typeof detail === 'string' ? detail : t('Unable to credit points right now.'), 'error');
    }
    setBusyAction('');
  };

  const syncRedeemFromPoints = (raw) => {
    const clean = String(raw || '').replace(/[^\d]/g, '');
    setRedeemPoints(clean);
    if (!clean) {
      setRedeemAmount('');
      return;
    }
    const num = Number(clean);
    if (!Number.isFinite(num)) return;
    setRedeemAmount((num / 100).toFixed(2));
  };

  const syncRedeemFromAmount = (raw) => {
    const clean = String(raw || '').replace(/[^0-9.]/g, '');
    setRedeemAmount(clean);
    if (!clean) {
      setRedeemPoints('');
      return;
    }
    const num = Number(clean);
    if (!Number.isFinite(num)) return;
    setRedeemPoints(String(Math.round(num * 100)));
  };

  const runRedeem = async () => {
    if (!selectedRedeemCustomerId) return;
    const pts = Math.round(Math.max(Number(redeemPoints || 0), 0));
    if (Number.isNaN(pts) || pts < 100) {
      setToastMessage(t('Minimum 100 points required.'), 'error');
      return;
    }

    setBusyAction('redeem');
    try {
      const init = await apiMerchantInitiateRedemption(selectedRedeemCustomerId, pts, applyBonusOnRedemption);
      await apiValidateRedemption('3457', init?.redemption_id || null);
      setRedeemPoints('');
      setRedeemAmount('');
      setApplyBonusOnRedemption(false);
      await fetchData(true);
      setToastMessage(t('Redemption completed successfully.'));
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setToastMessage(typeof detail === 'string' ? detail : t('Unable to initiate redemption.'), 'error');
    }
    setBusyAction('');
  };

  const approvePending = async (pendingId) => {
    if (!pendingId) return;
    setBusyAction(`approve-${pendingId}`);
    try {
      await apiValidateRedemption('3457', pendingId);
      await fetchData(true);
      setToastMessage(t('Request approved.'));
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setToastMessage(typeof detail === 'string' ? detail : t('Validation failed.'), 'error');
    }
    setBusyAction('');
  };

  const rejectPending = async (pendingId) => {
    if (!pendingId) return;
    setBusyAction(`reject-${pendingId}`);
    try {
      await apiRejectRedemption(pendingId);
      setRejectedPendingIds((prev) => ({ ...prev, [pendingId]: true }));
      await fetchData(true);
      setToastMessage(t('Request rejected.'));
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setToastMessage(typeof detail === 'string' ? detail : t('Unable to reject request right now.'), 'error');
    }
    setBusyAction('');
  };

  return (
    <div className="space-y-6 rounded-2xl bg-gray-50 p-4 md:p-6" data-testid="merchant-rewards-page">
      <style>{`
        .no-spinner::-webkit-outer-spin-button,
        .no-spinner::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        .no-spinner {
          -moz-appearance: textfield;
        }
        @keyframes flybackAiPulse {
          0% { transform: rotate(0deg) scale(1); filter: drop-shadow(0 0 0 rgba(var(--brand-rgb),0)); }
          25% { transform: rotate(-8deg) scale(1.08); filter: drop-shadow(0 0 8px rgba(var(--brand-rgb),0.35)); }
          50% { transform: rotate(6deg) scale(1.14); filter: drop-shadow(0 0 14px rgba(var(--brand-rgb),0.45)); }
          75% { transform: rotate(-5deg) scale(1.08); filter: drop-shadow(0 0 8px rgba(var(--brand-rgb),0.35)); }
          100% { transform: rotate(0deg) scale(1); filter: drop-shadow(0 0 0 rgba(var(--brand-rgb),0)); }
        }
        @keyframes flybackAiTipGlow {
          0%, 100% {
            box-shadow: 0 0 0 0 rgba(var(--brand-rgb), 0.06), 0 8px 24px rgba(var(--brand-rgb), 0.10);
          }
          50% {
            box-shadow: 0 0 0 1px rgba(var(--brand-rgb), 0.35), 0 14px 36px rgba(var(--brand-rgb), 0.24);
          }
        }
        @keyframes flybackAiTipBorderFlow {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>

      <CenteredStatusPopup message={toast} type={toastType} />

      <div>
        <h1 className="text-4xl font-bold tracking-tight text-[#0f172a]" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>
          {t('Rewards')}
        </h1>
        <p className="text-sm text-stone-500">{t('Simulate order credits, redeem in person, and validate pending redemption requests.')}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm space-y-3">
          <p className="font-semibold text-sm text-stone-800">{t('Simulate & Credit')}</p>
          <CustomerCombobox
            customers={customers}
            value={selectedSimulateCustomerId}
            onChange={setSelectedSimulateCustomerId}
            placeholder={t('Select customer...')}
            searchPlaceholder={t('Search customer')}
            emptyText={t('No customer found.')}
            testId="simulate-customer-select"
          />
          <Input value={orderId} onChange={(e) => setOrderId(e.target.value.toUpperCase())} placeholder={t('ORD-123')} className="h-10 rounded-xl border-gray-200" />
          <Input type="number" min="1" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} onWheel={(e) => e.currentTarget.blur()} placeholder={t('Amount')} className="no-spinner h-10 rounded-xl border-gray-200" />
          <button onClick={runSimulateOrder} disabled={busyAction === 'simulate' || loading} className="w-full h-10 rounded-xl bg-brand hover:brightness-95 text-white text-sm font-semibold transition disabled:opacity-60">
            {busyAction === 'simulate' ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : t('Credit Points')}
          </button>
        </div>

        <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm space-y-3">
          <p className="font-semibold text-sm text-stone-800">{t('Redeem In Person')}</p>
          <CustomerCombobox
            customers={customers}
            value={selectedRedeemCustomerId}
            onChange={setSelectedRedeemCustomerId}
            placeholder={t('Select customer...')}
            searchPlaceholder={t('Search customer')}
            emptyText={t('No customer found.')}
            testId="redeem-customer-select"
          />
          <Input
            type="number"
            min="100"
            step="1"
            value={redeemPoints}
            onChange={(e) => syncRedeemFromPoints(e.target.value)}
            onWheel={(e) => e.currentTarget.blur()}
            placeholder={t('Points')}
            className="no-spinner h-10 rounded-xl border-gray-200"
          />
          <Input
            type="number"
            min="1"
            step="0.01"
            value={redeemAmount}
            onChange={(e) => syncRedeemFromAmount(e.target.value)}
            onWheel={(e) => e.currentTarget.blur()}
            placeholder={t('Amount')}
            className="no-spinner h-10 rounded-xl border-gray-200"
          />

          <button onClick={runRedeem} disabled={busyAction === 'redeem' || loading} className="w-full h-10 rounded-xl bg-brand hover:brightness-95 text-white text-sm font-semibold transition disabled:opacity-60">
            {busyAction === 'redeem' ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : t('Redeem')}
          </button>
        </div>

        <div
          className="rounded-xl border p-4 shadow-sm space-y-3"
          style={{
            borderColor: 'rgba(var(--brand-rgb), 0.30)',
            background: 'linear-gradient(135deg, rgba(var(--brand-rgb), 0.16), rgba(255,255,255,0.98), rgba(var(--brand-rgb), 0.08))',
            backgroundSize: '220% 220%',
            animation: 'flybackAiTipGlow 2.8s ease-in-out infinite, flybackAiTipBorderFlow 4s ease-in-out infinite',
          }}
        >
          <div className="flex items-center gap-2">
            <span className="inline-flex h-8 w-8 rounded-full bg-white items-center justify-center border border-brand/20">
              <Sparkles className="w-4 h-4 text-brand" style={{ animation: 'flybackAiPulse 2.7s ease-in-out infinite' }} />
            </span>
            <p className="font-semibold text-sm text-brand">{t('AI Tip')}</p>
          </div>
          <p className="text-sm leading-relaxed text-stone-700">
            {t('Offering 10% point bonus on redemption increases return visit rate by ~20%.')}
          </p>
          <label className="inline-flex items-center gap-2 text-xs font-semibold text-brand">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-brand/30 text-brand"
              checked={applyBonusOnRedemption}
              onChange={(e) => setApplyBonusOnRedemption(e.target.checked)}
            />
            {t('Apply this bonus on redemption')}
          </label>
        </div>
      </div>

      <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-semibold text-stone-800">{t('Customer-Initiated Queue')}</p>
          <Badge className="rounded-full bg-brand-soft text-brand border border-brand/20">{queue.length}</Badge>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-gray-100">
                {['Customer', 'Requested Points', 'Channel', 'Created At', 'Actions'].map((h) => (
                  <th key={h} className="px-3 py-2 text-xs uppercase tracking-wider font-semibold text-gray-500">{t(h)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!queue.length ? (
                <tr>
                  <td colSpan={5} className="px-3 py-12 text-center">
                    <div className="flex flex-col items-center gap-2 text-stone-500">
                      <span className="h-10 w-10 rounded-full bg-stone-100 flex items-center justify-center">
                        <Inbox className="w-5 h-5 text-stone-400" />
                      </span>
                      <p className="text-sm">{t('No pending redemption requests.')}</p>
                    </div>
                  </td>
                </tr>
              ) : queue.map((row) => (
                <tr key={row.id} className="border-b border-gray-100">
                  <td className="px-3 py-2.5">{row.customer_name}</td>
                  <td className="px-3 py-2.5">{Number(row.points || 0).toLocaleString()} {t('pts')}</td>
                  <td className="px-3 py-2.5 capitalize">{row.channel || 'source'}</td>
                  <td className="px-3 py-2.5">{row.created_at ? new Date(row.created_at).toLocaleString() : t('N/A')}</td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => approvePending(row.id)}
                        disabled={busyAction === `approve-${row.id}`}
                        className="h-8 px-2.5 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 text-xs font-semibold inline-flex items-center gap-1"
                      >
                        {busyAction === `approve-${row.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} {t('Approve')}
                      </button>
                      <button
                        type="button"
                        onClick={() => rejectPending(row.id)}
                        disabled={busyAction === `reject-${row.id}`}
                        className="h-8 px-2.5 rounded-lg border border-rose-200 bg-rose-50 text-rose-700 text-xs font-semibold disabled:opacity-60 inline-flex items-center justify-center"
                      >
                        {busyAction === `reject-${row.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : t('Reject')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <p className="text-sm font-semibold text-stone-800">{t('Voice Agent Loyalty Connect')}</p>
              <span className="rounded-full bg-amber-100 text-amber-700 px-2 py-0.5 text-xs font-medium">{t('Coming Soon')}</span>
            </div>
            <p className="text-xs text-stone-500 mt-1">{t("Powered by ISO's AI Agent")}</p>
            <p className="text-xs text-stone-600 mt-1 italic">{t('Auto-enroll via call: "You earned 500 points worth $5 — say YES to join".')}</p>
          </div>
          <PhoneCall className="w-5 h-5 text-stone-500 mt-0.5" />
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <label className="h-10 rounded-xl border border-stone-200 bg-stone-50 px-3 text-xs font-semibold flex items-center justify-between gap-3 min-w-[140px]">
            {t('Agent')}
            <input
              type="checkbox"
              checked={voiceConfig.enabled}
              onChange={(e) => setVoiceConfig((prev) => ({ ...prev, enabled: e.target.checked }))}
            />
          </label>
          <select className="h-10 rounded-xl border border-stone-200 bg-white px-3 text-xs min-w-[150px]" value={voiceConfig.target} onChange={(e) => setVoiceConfig((prev) => ({ ...prev, target: e.target.value }))}>
            <option value="all_callers">{t('All callers')}</option>
            <option value="members">{t('Members')}</option>
            <option value="lost_bucket">{t('Lost bucket')}</option>
            <option value="risk_bucket">{t('At Risk bucket')}</option>
            <option value="active_bucket">{t('Active bucket')}</option>
          </select>
          <select className="h-10 rounded-xl border border-stone-200 bg-white px-3 text-xs min-w-[90px]" value={voiceConfig.multiplier} onChange={(e) => setVoiceConfig((prev) => ({ ...prev, multiplier: e.target.value }))}>
            <option value="1x">1x</option>
            <option value="2x">2x</option>
          </select>
        </div>
      </div>
    </div>
  );
}
